import classNames from 'classnames';

import * as api from '../../../../api';
import * as store from '../../../../reducers/store';

import { Avatar, Link, Typography } from '@vivaahealth/design-system';
import { Link as ReactRouterLink } from 'react-router-dom';
import { styled } from 'styled-components';
import { useSelector } from 'react-redux';
import { Col, Row } from 'react-bootstrap';

import Preferences from '../Common/Preferences';
import Profile from '../Common/Profile';
import ScheduleAppointmentButton from '../Common/ScheduleAppointmentButton';
import StarRating from '../Common/StarRating';

import { formatProviderSpecialties } from 'common/dist/utils/provider';
import { ORGANIZATION_ID_TO_PROFILE_PATH } from '../Common';
import { getPrevisitAsset } from '../../../../utils';
import { generateGoogleMapsDirectionURL } from '../../../../utils/location';
import {
  getProviderInitials,
  getProviderNameFriendly,
} from '../../../../utils/provider';

interface Props {
  provider: api.PrevisitProvider.SafeProviderWithPractices;
  idx: number;
}

const ResultsItem: React.FC<Props> = ({ provider, idx }) => {
  const { previsitImageUrl, profile, practices, preference, rating } = provider;
  const distance = practices[0].distance;
  const providerName = getProviderNameFriendly(provider, [
    'firstName',
    'lastName',
    'suffix',
  ]);
  const providerSpecialties = formatProviderSpecialties(profile?.specialties);

  const reputationRating = rating?.reputation;
  const embedded = useSelector((store: store.All) => store.embedded);
  const organizationId = useSelector(
    (store: store.All) => store.inbound.organizationId,
  );
  const setStore = () => {
    sessionStorage.setItem(
      'specialty',
      providerSpecialties ? providerSpecialties : '',
    );
    sessionStorage.setItem('provider', providerName);
  };
  const providerResultContent = (
    <Row>
      <Col xs="auto" className="ps-0 pe-2">
        <StyledAvatar
          alt={getProviderNameFriendly(provider, [
            'firstName',
            'lastName',
            'prefix',
          ])}
          src={
            previsitImageUrl ? getPrevisitAsset(previsitImageUrl) : undefined
          }
          initials={getProviderInitials(provider)}
        />
      </Col>
      <Col className="py-0 px-2">
        {embedded ? (
          <StyledEmbeddedTitleLarge variant="paragraph-large" bold>
            {providerName}
          </StyledEmbeddedTitleLarge>
        ) : (
          <StyledTitleLarge variant="paragraph-large" bold>
            {providerName}
          </StyledTitleLarge>
        )}
        <div className="pb-1">
          <Typography variant="title-medium">
            {!!provider.profile?.jobTitle
              ? provider.profile.jobTitle
              : providerSpecialties ?? ''}
          </Typography>
        </div>
        {reputationRating && <StarRating ratingWithCount={reputationRating} />}
        <Preferences preference={preference} />
        <Profile
          profile={profile}
          agesServed={provider.preference?.agesServed}
        />
      </Col>
    </Row>
  );

  return (
    <StyledRow
      className={'mx-3 mx-sm-0' + classNames({ ' border-top': idx !== 0 })}
    >
      <Col className="my-4">
        {embedded ? (
          <ProviderProfileLink
            target={'_blank'}
            href={`${ORGANIZATION_ID_TO_PROFILE_PATH[organizationId]}/${preference?.slug}`}
            onClick={() => setStore()}
          >
            {providerResultContent}
          </ProviderProfileLink>
        ) : (
          <StyledLink
            className="text-decoration-none"
            to={`./${preference?.slug}`}
            onClick={() => setStore()}
          >
            {providerResultContent}
          </StyledLink>
        )}
        <Typography variant="paragraph-medium" className="pt-1">
          <PracticeLink
            small
            target="_blank"
            href={generateGoogleMapsDirectionURL({
              address: practices[0].address,
              city: practices[0].city,
              state: practices[0].state,
              zip: practices[0].zip,
            })}
          >
            {practices[0].name + (distance ? `, ${distance.toFixed(1)}mi` : '')}
          </PracticeLink>
        </Typography>
      </Col>
      <Col xs={12} sm="auto" className="py-sm-4 pb-3 px-0 ps-sm-3 pe-sm-0">
        <ScheduleAppointmentButton
          schedulingLink={provider.preference?.schedulingLink}
          practicePhoneNumber={practices[0].phoneNumber}
          fullWidth={true}
        />
      </Col>
    </StyledRow>
  );
};

export default ResultsItem;

const StyledAvatar = styled(Avatar)`
  height: 80px !important;
  width: 80px !important;
  border-color: var(--color-shade-60);
`;

const ProviderProfileLink = styled.a`
  color: white !important;
  font-size: 14px !important;
`;

const StyledEmbeddedTitleLarge = styled(Typography)`
  ${ProviderProfileLink}:hover & {
    text-decoration: underline;
  }
`;

const StyledLink = styled(ReactRouterLink)``;

const StyledTitleLarge = styled(Typography)`
  ${StyledLink}:hover & {
    text-decoration: underline;
  }
`;

const StyledRow = styled(Row)`
  border-color: var(--color-border-default-light) !important;
`;

const PracticeLink = styled(Link)`
  color: var(--color-content-default-dark);
  font-weight: normal;
  position: relative;
  left: 84px;
  text-decoration-line: underline;
`;
